import { Provider as ReduxProvider } from 'react-redux';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';

import { store, type AppStore } from '@/app/store';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';

import { ThemeProvider } from './ThemeProvider';

export interface AppProvidersProps {
  children: ReactNode;
  /** Injectable store, so tests can supply an isolated instance. */
  store?: AppStore;
}

/**
 * Composes every application-wide provider in one place.
 *
 * Order matters: the outermost `ErrorBoundary` must sit above the providers so
 * a failure while a provider initializes still renders a fallback instead of a
 * blank page. Redux comes next because `ThemeProvider` reads from the store.
 */
export function AppProviders({ children, store: storeInstance = store }: AppProvidersProps) {
  return (
    <ErrorBoundary>
      <ReduxProvider store={storeInstance}>
        <ThemeProvider>
          <TooltipProvider delayDuration={300}>{children}</TooltipProvider>
        </ThemeProvider>
      </ReduxProvider>
    </ErrorBoundary>
  );
}
